import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { CreateWorkOrderForm } from '../CreateWorkOrderForm';
import { WorkOrderConfirmModal } from './WorkOrderConfirmModal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { CreateWorkOrderData, WorkOrder } from '../../../types/workOrder';
import { DownloadLabelButton } from '../../../components/labels/DownloadLabelButton';
import { WorkOrderLabelDocument } from '../../../components/labels/WorkOrderLabelDocument';
import { workOrderScanUrl } from '../../../utils/labelUrl';

interface Props {
  editingOrder: WorkOrder | null;
  isEditingLocked: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => void;
  canWrite?: boolean;
}

export const EditWorkOrderModal: React.FC<Props> = ({ editingOrder, isEditingLocked, onClose, onSubmit, canWrite = true }) => {
  const { t } = useLanguage();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = React.useState(false);

  const isReadOnly = editingOrder?.status === 'completed' || editingOrder?.status === 'cancelled';

  const requestClose = () => {
    if (!editingOrder) return;
    if (isReadOnly) {
      onClose();
      return;
    }
    setIsCloseConfirmOpen(true);
  };

  const confirmClose = () => {
    setIsCloseConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={!!editingOrder}
        onClose={requestClose}
        title={isReadOnly ? t('workOrders.page.viewWorkOrder') : t('workOrders.page.updateWorkOrder')}
        headerAction={
          editingOrder ? (
            <DownloadLabelButton
              scanUrl={workOrderScanUrl(editingOrder.id)}
              renderDocument={(qr) => <WorkOrderLabelDocument workOrder={editingOrder} qrDataUrl={qr} />}
              fileName={`work-order-label-${editingOrder.workOrderNumber || editingOrder.id}`}
              label={t('labels.downloadLabel')}
              data-test-id="work-order-download-label"
            />
          ) : undefined
        }
        maxWidth='7xl'
      >
        <CreateWorkOrderForm
          initialData={editingOrder || undefined}
          onSubmit={(data, action) => {
            if (isEditingLocked) return;
            onSubmit(data, action);
          }}
          onCancel={onClose}
          readOnly={!canWrite}
        />
      </Modal>

      <WorkOrderConfirmModal
        isOpen={isCloseConfirmOpen}
        onClose={() => setIsCloseConfirmOpen(false)}
        title={t('workOrders.closeEditConfirmModal.title')}
        description={t('workOrders.closeEditConfirmModal.description')}
        cancelLabel={t('workOrders.closeEditConfirmModal.keepEditing')}
        confirmLabel={t('workOrders.closeEditConfirmModal.discard')}
        onConfirm={confirmClose}
        testId="wo-close-edit-confirm-modal"
        cancelTestId="wo-close-edit-confirm-keep-editing"
        confirmTestId="wo-close-edit-confirm-discard"
      />
    </>
  );
};
